'use client';

import { PlayVideoButton } from '@/components/blocks/VideoDisplay/PlayVideoButton';
import {
	dotsVideoGallerySX,
	playOverlaySelectedSX,
	playOverlaySX,
	thumbnailContainerSX,
	thumbnailGalleryWrapperSX,
	thumbnailImgSX,
	thumbnailSelectedContainerSX,
	thumbnailSelectedImgSX,
} from '@/components/blocks/VideoDisplay/styles/videoStyle';
import { Attachment } from '@/data/types/Product';
import { getErrorImg, getVideoThumbnail, getYoutubeId } from '@/utils/video';
import { Box, IconButton, Stack } from '@mui/material';
import { FC } from 'react';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { useVideoGallery } from '@/components/blocks/ProductDetails/context/VideoGalleryContext';
import {
	buttonNavigationNextSx,
	buttonNavigationPrevSx,
} from '@/components/blocks/ProductDetails/styles/carousel';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useVisibleSlides } from '@/components/blocks/ProductCarousel/utils/useVisibleSlides';
import 'swiper/css';

interface ThumbnailGalleryProps {
	videos: Attachment[];
	handleChangeVideo: (videoData: Attachment) => void;
	handlePrevVideo: () => void;
	handleNextVideo: () => void;
}

export const ThumbnailGallery: FC<ThumbnailGalleryProps> = ({
	videos,
	handleChangeVideo,
	handlePrevVideo,
	handleNextVideo,
}) => {
	const { actualVideo, swiperRefVideoGallery } = useVideoGallery();
	const { lgMdMatch: isMobileOrTablet } = useVisibleSlides();
	const currentVideoSlide =
		videos.findIndex((video) => video.attachmentAssetPath === actualVideo?.attachmentAssetPath) ??
		-1;
	return (
		<Box sx={thumbnailGalleryWrapperSX}>
			{isMobileOrTablet ? (
				// eslint-disable-next-line react/forbid-component-props
				<Stack
					direction="row"
					justifyContent="center"
					spacing={1}
					mt={2}
					id={'mediaVideoNavDots'}
					className="media-video__navigation-dot"
				>
					{videos.map((video, index) => {
						const isActive = index === currentVideoSlide;
						return (
							<Box
								key={video.attachmentAssetPath}
								onClick={() => handleChangeVideo(video)}
								sx={dotsVideoGallerySX(isActive)}
								data-video-index={index}
							/>
						);
					})}
				</Stack>
			) : (
				<Swiper
					onSwiper={(instance: SwiperType) => {
						swiperRefVideoGallery.current = instance;
					}}
					modules={[FreeMode]}
					freeMode
					allowTouchMove
					spaceBetween={12}
					slidesPerView="auto"
					id="mediaVideoThumbnails"
					className="custom-scrollbar media-video__thumbnail"
				>
					{videos.map((video, index) => {
						const videoId = getYoutubeId(video.attachmentAssetPath || '');
						if (!videoId) return null;

						const isSelected = video.attachmentAssetPath === actualVideo?.attachmentAssetPath;
						const thumbUrl = getVideoThumbnail(videoId);

						return (
							<SwiperSlide key={video.attachmentAssetPath}>
								<Box
									sx={isSelected ? thumbnailSelectedContainerSX : thumbnailContainerSX}
									key={video.attachmentAssetID}
									onClick={() => handleChangeVideo(video)}
									className={isSelected ? 'media-video__thumbnail--selected' : ''}
									data-video-index={index}
								>
									<Box
										component="img"
										src={thumbUrl}
										sx={isSelected ? thumbnailSelectedImgSX : thumbnailImgSX}
										onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
											e.currentTarget.src = getErrorImg(320, 180, 'e0e0e0', '333', 'Error');
										}}
									/>

									<Box
										sx={isSelected ? playOverlaySelectedSX : playOverlaySX}
										id={'mediaVideoPlay'}
									>
										<PlayVideoButton size={24} className="text-white opacity-90" />
									</Box>
								</Box>
							</SwiperSlide>
						);
					})}

					<Box className="media-video__thumbnail" id="mediaVideoThumbnails">
						<IconButton
							id="mediaVideoThumbnailsControlPrev"
							sx={buttonNavigationPrevSx}
							onClick={handlePrevVideo}
						>
							<DynamicIcon name="chevronLeft" color="white" />
						</IconButton>

						<IconButton
							id="mediaVideoThumbnailsControlNext"
							sx={buttonNavigationNextSx}
							onClick={handleNextVideo}
						>
							<DynamicIcon name="chevronRight" color="white" />
						</IconButton>
					</Box>
				</Swiper>
			)}
		</Box>
	);
};
